<template>
  <div class="scroll__container"
       @mousemove="watchMouse"
       @mouseleave="mouseLeave"
       @mouseenter="mouseEnter"
       @touchstart="mouseEnter"
       @touchend="mouseLeave"
       @scroll="update"
  >
    <slot />
    <transition name="y">
      <div v-show="y && (show || !hide)" ref="y" :class="classY" @mousedown="dragStartY" />
    </transition>
    <transition name="x">
      <div v-show="x && (show || !hide)" ref="x" :class="classX" @mousedown="dragStartX" />
    </transition>
  </div>
</template>

<script>
export default {
  name: 'Scroll',
  props: {
    minSize: { type: Number, default: 4 },
    expandSlider: { type: Boolean, default: true },
    expandTolerance: { type: Number, default: 30 },
    y: { type: Boolean, default: true },
    x: { type: Boolean, default: false },
    left: { type: Boolean, default: false },
    hide: { type: Boolean, default: false },
  },
  data() {
    return {
      hover: false,
      hoverY: false,
      hoverX: false,
      drag: false,
      hideTimer: null,
      show: true,
      prev: {
        left: null,
        top: null,
        drag: 0,
      },
    };
  },
  computed: {
    classY() {
      return {
        'scroll__slider-y': true,
        'scroll__slider-y--hover': this.hoverY && this.expandSlider,
        'scroll__slider-y--left': this.left,
      };
    },
    classX() {
      return {
        'scroll__slider-x': true,
        'scroll__slider-x--hover': this.hoverX && this.expandSlider,
      };
    }
  },
  mounted() {
    this.static = { x: {}, y: {}, box: {} };
    this.update(true);
    this.doHideDelayed();
    window.addEventListener('resize', this.resize, true);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resize, true);
  },
  methods: {
    doHide() {
      if (this.hide) this.show = false;
    },
    doHideDelayed() {
      if (!this.hide) return;
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => { this.doHide(); }, 1000);
    },
    mouseEnter() {
      clearTimeout(this.hideTimer);
      this.hover = true;
      this.show = true;
      this.update(true);
    },
    mouseLeave() {
      this.hover = false;
      if (!this.drag) {
        this.hoverX = false;
        this.hoverY = false;
        this.doHideDelayed();
      }
    },
    watchMouse(event) {
      if (this.drag) return;
      const { box } = this.static;
      if (this.y) {
        const dist = this.left ? (event.clientX - box.left) : (box.right - event.clientX);
        this.hoverY = dist < this.expandTolerance;
      }
      if (this.x) {
        this.hoverX = (box.bottom - event.clientY) < this.expandTolerance;
      }
    },
    updateSlider(y) {
      const coord = y ? 'y' : 'x';
      const container = y ? this.$el.clientHeight : this.$el.clientWidth;
      const content = y ? this.$el.scrollHeight : this.$el.scrollWidth;
      if (container === this.static[coord].container && content === this.static[coord].content) {
        return false;
      }
      const param = y ? 'height' : 'width';
      const enable = content > container;
      const size = enable ? Math.max(this.minSize, container * (container / content)) : 0;
      if (this.$refs[coord]) {
        this.$refs[coord].style[param] = `${size}px`;
        this.static[coord] = {
          rate: (container - size - 4) / (content - container), enable, content, container,
        };
      }
      return true;
    },
    resize() {
      this.prev.left = null;
      this.prev.top = null;
      this.update();
    },
    update(init) {
      const content = this.$el;
      const needUpdateX = (this.x && this.prev.left !== content.scrollLeft);
      const needUpdateY = (this.y && this.prev.top !== content.scrollTop);
      const doUpdate = needUpdateX || needUpdateY;
      if (doUpdate || init) {
        this.static = {
          x: {},
          y: {},
          box: this.$el.getBoundingClientRect(),
          window: {
            height: window.innerHeight,
            width: window.innerWidth
          }
        };
      }
      if (!doUpdate && !init) return false;

      const { scrollLeft, scrollTop, clientHeight, clientWidth } = content;
      const { height, width } = this.static.box;

      const changedX = this.updateSlider();
      if (this.static.x.enable) {
        this.$refs.x.style.left = `${scrollLeft + scrollLeft * this.static.x.rate}px`;
        this.$refs.x.style.bottom = `${clientHeight - height - scrollTop}px`;
      }
      const changedY = this.updateSlider(true);
      if (this.static.y.enable) {
        this.$refs.y.style.top = `${scrollTop + scrollTop * this.static.y.rate}px`;
        if (this.left) {
          this.$refs.y.style.right = 'unset';
          this.$refs.y.style.left = `${scrollLeft}px`;
        } else {
          this.$refs.y.style.left = 'unset';
          this.$refs.y.style.right = `${clientWidth - width - scrollLeft }px`;
        }
      }

      if (changedX || changedY) {
        this.static.box = this.$el.getBoundingClientRect();
      }

      this.$emit('scroll', {
        left: content.scrollLeft,
        top: content.scrollTop,
        delta: {
          left: content.scrollLeft - (this.prev.left || 0),
          top: content.scrollTop - (this.prev.top || 0),
        },
      });
      this.prev.left = content.scrollLeft;
      this.prev.top = content.scrollTop;
      return true;
    },
    dragStartY(event) {
      this.prev.drag = event.clientY;
      this.drag = true;
      document.addEventListener('mousemove', this.dragDoY);
      document.addEventListener('mouseup', this.dragStop);
    },
    dragStartX(event) {
      this.prev.drag = event.clientX;
      this.drag = true;
      document.addEventListener('mousemove', this.dragDoX);
      document.addEventListener('mouseup', this.dragStop);
    },
    dragStop(event) {
      this.drag = false;
      this.hoverX = false;
      this.hoverY = false;
      this.watchMouse(event);
      document.removeEventListener('mousemove', this.dragDoX);
      document.removeEventListener('mousemove', this.dragDoY);
      document.removeEventListener('mouseup', this.dragStop);
      if (!this.hover) {
        this.doHide();
      }
    },
    dragDoY(event) {
      event.preventDefault();
      const rate = this.$el.scrollHeight / this.$el.offsetHeight;
      const delta = (event.clientY - this.prev.drag) * rate;
      this.$el.scrollBy(0, delta);
      this.prev.drag = event.clientY;
    },
    dragDoX(event) {
      event.preventDefault();
      const rate = this.$el.scrollWidth / this.$el.offsetWidth;
      const delta = (event.clientX - this.prev.drag) * rate;
      this.$el.scrollBy(delta, 0);
      this.prev.drag = event.clientX;
    },
  },
};
</script>

<style scoped>
.scroll__container {
  position: relative;
  overflow: scroll;
  -ms-overflow-style: none;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
}
.scroll__container::-webkit-scrollbar {
  display: none;
}

.scroll__slider-x, .scroll__slider-y {
  position: absolute;
  opacity: 0.3;
  border-radius: 10px;
  width: 7px;
  height: 7px;
  background: rgba(0,0,0,0.5);
  margin:2px;
  transition: width 0.5s, height 0.5s, background 0.5s;
}

.scroll__slider-y {
  height: 0;
}

.scroll__slider-x {
  width: 0;
}

.scroll__slider-y--hover {
  width:15px;
  background: rgba(0,0,0,0.8);
}

.scroll__slider-x--hover {
  height:15px;
  background: rgba(0,0,0,0.8);
}

.x-enter-active, .y-enter-active { transition: all 0.2s ease; }
.x-leave-active, .y-leave-active { transition: all 0.5s ease; }
.y-enter, .y-leave-to { transform: translateX(100%); }
.scroll__slider-y--left.y-enter, .scroll__slider-y--left.y-leave-to{
  transform: translateX(-100%);
}

.x-enter, .x-leave-to { transform: translateY(100%); }

</style>
